import { createFileRoute } from '@tanstack/react-router';
import { STEP_KEYS, type ImportMethod, type WizardStep } from '@/pages/Entities/bulk-import/store';
import { IMPORT_MODES, type ImportMode } from '@/pages/ImportCenter/import-modes';
import { CP_STEPS, type ClientPortfolioStep } from '@/pages/ImportCenter/client-portfolios/client-portfolio-search';

export interface ImportCenterSearch {
  /** Which import mode is open. Absent → the mode-selection landing. */
  mode?: ImportMode;
  /** Destination client portfolio (context tenant id) for the single-client flow. */
  client?: string;
  /** Bulk-import wizard stage, reused from `/entities/bulk-import`. */
  step?: WizardStep;
  method?: ImportMethod;
  /** Client-portfolio flow: the step before an import exists. Absent → the import list. */
  cp?: ClientPortfolioStep;
  /** Client-portfolio flow: the chosen destination manager tenant (SA only; an AGENCY is always itself). */
  destination?: string;
  /** Client-portfolio flow: the open import. Its status picks the view. */
  importId?: string;
}

const nonEmpty = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/**
 * `/import-center` — agency import center.
 *
 * URL owns navigation, as in `/entities/bulk-import`: `mode` picks the flow, `client` the destination
 * portfolio, `step` + `method` the wizard stage. Only modes marked live in `IMPORT_MODES` are
 * reachable; an upcoming mode in the URL falls back to the landing.
 */
export const Route = createFileRoute('/import-center')({
  validateSearch: (search: Record<string, unknown>): ImportCenterSearch => {
    const mode = IMPORT_MODES.find((m) => m.key === search.mode && m.live)?.key;
    if (!mode) return {};
    if (mode === 'client-portfolios') {
      // The flow's own params only; the single-client wizard's never leak in. Persona-blind (FR-1):
      // the page applies the persona rule.
      const cp = CP_STEPS.includes(search.cp as ClientPortfolioStep) ? (search.cp as ClientPortfolioStep) : undefined;
      const destination = nonEmpty(search.destination);
      const importId = nonEmpty(search.importId);
      return {
        mode,
        ...(importId ? { importId } : cp ? { cp } : {}),
        ...(destination ? { destination } : {}),
      };
    }
    const client = typeof search.client === 'string' && search.client ? search.client : undefined;
    const method =
      search.method === 'own-file' || search.method === 'template'
        ? (search.method as ImportMethod)
        : undefined;
    const step = STEP_KEYS.includes(search.step as WizardStep) ? (search.step as WizardStep) : 'method';
    // A wizard stage past `method` is meaningless without a destination and a chosen flow.
    return { mode, client, method, step: client && method ? step : 'method' };
  },
});
